import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getDownloadURL, ref } from "firebase/storage";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { storage } from "../../services/firebase"; // Asegúrate de que esto esté configurado

const ChatbotButton = React.memo(
  ({
    onClick,
    position = { bottom: 20, right: 20 },
    size = 60,
    showBadge = false,
    logoSrc = "images/chatbot/vic-logo.png", // Ruta en Storage
  }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [logoUrl, setLogoUrl] = useState("");
    const [logoError, setLogoError] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    // Cargar imagen con estrategia de respaldo múltiple
    useEffect(() => {
      let isMounted = true;
      const fetchLogo = async () => {
        try {
          // ESTRATEGIA 1: Intenta primero desde carpeta pública
          const publicPath = "/assets/images/chatbot/vic-logo.png";

          // Comprobar primero si la imagen pública existe
          const checkPublicImage = () => {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
              img.src = publicPath;
            });
          };

          const publicImageExists = await checkPublicImage();

          if (publicImageExists && isMounted) {
            // Verificar isMounted
            setLogoUrl(publicPath);
            return;
          }

          // ESTRATEGIA 2: Si falla, intenta desde Firebase Storage
          const logoRef = ref(storage, logoSrc);
          const url = await getDownloadURL(logoRef);
          if (isMounted) {
            // Verificar isMounted
            setLogoUrl(url);
          }
        } catch (error) {
          if (isMounted) {
            // Verificar isMounted
            setLogoError(true);
          }

          // ESTRATEGIA 3: Último recurso - verificar si existe el logo genérico
          try {
            const fallbackPath = "/assets/logo.png";
            const img = new Image();
            img.onload = () => {
              if (isMounted) {
                // Verificar isMounted
                setLogoUrl(fallbackPath);
                setLogoError(false);
              }
            };
            img.onerror = () => {
              // Mantiene logoError=true para usar el icono SmartToy
            };
            img.src = fallbackPath;
          } catch (e) {
            console.error("Todos los intentos de carga de logo fallaron");
          }
        }
      };

      fetchLogo();

      // Cleanup para evitar actualizaciones en componente desmontado
      return () => {
        isMounted = false;
      };
    }, [logoSrc]);

    // Añadir tracking de eventos para análisis
    const handleClick = () => {
      // Registrar evento de clic para análisis
      if (typeof window.analytics !== "undefined") {
        window.analytics.track("Chatbot Button Click", {
          location: window.location.pathname,
        });
      }

      onClick();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    return (
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={{
          position: "fixed",
          bottom: position.bottom,
          right: position.right,
          zIndex: 1000,
        }}
      >
        <Box
          onClick={handleClick}
          aria-label={t("chatbot.openChat")}
          aria-describedby="chatbot-tooltip" // Conectar con el tooltip
          tabIndex={0} // Hacerlo focusable para navegación por teclado
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleClick();
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #3A8EBA 0%, #216E99 100%)"
                : "linear-gradient(135deg, #2979a3 0%, #195b80 100%)", // Más oscuro para mejor contraste
            border: `3px solid ${theme.palette.background.paper}`,
            position: "relative",
            transition: "all 0.3s ease",
            "@media (max-width: 600px)": {
              width: Math.max(size * 0.9, 50), // Asegurar tamaño mínimo en móviles
              height: Math.max(size * 0.9, 50),
            },
            "&:hover": {
              boxShadow: "0 6px 25px rgba(0,0,0,0.3)",
            },
            "&:hover + .chat-tooltip": {
              opacity: 1,
              transform: "translateX(-20px) scale(1)",
              visibility: "visible",
            },
            "&:focus-visible": {
              // Añadir indicador visual de foco
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "2px",
            },
          }}
        >
          <Box
            component="img"
            src={
              logoError
                ? "/assets/images/chatbot/vic-logo.png"
                : logoUrl || "/assets/images/chatbot/vic-logo.png"
            }
            alt="Vic - Asistente"
            onError={(e) => {
              // Si la imagen falla incluso con fallback, usar una imagen en base64 o SVG inline
              e.currentTarget.src = "data:image/svg+xml;base64,..."; // Base64 de un icono simple
              e.currentTarget.onerror = null; // Prevenir bucles infinitos
            }}
            sx={{
              width: size * 0.75,
              height: size * 0.75,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />

          {showBadge && (
            <Box
              sx={{
                position: "absolute",
                top: -5,
                right: -5,
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "#ff5252",
                border: "2px solid #FFFFFF",
                animation: "pulse 1.5s infinite",
                "@keyframes pulse": {
                  "0%": {
                    boxShadow: "0 0 0 0 rgba(255, 82, 82, 0.7)",
                  },
                  "70%": {
                    boxShadow: "0 0 0 10px rgba(255, 82, 82, 0)",
                  },
                  "100%": {
                    boxShadow: "0 0 0 0 rgba(255, 82, 82, 0)",
                  },
                },
              }}
              aria-label={t("chatbot.newMessages")}
            />
          )}
        </Box>

        {/* Tooltip / Bocadillo animado */}
        <Box
          className="chat-tooltip"
          role="tooltip"
          id="chatbot-tooltip"
          aria-hidden={true} // Se mostrará con hover, no debe ser anunciado inicialmente
          sx={{
            position: "absolute",
            right: size + 15,
            bottom: size / 2 - 15,
            backgroundColor: "#FFFFFF",
            color: theme.palette.text.primary,
            padding: "10px 15px",
            borderRadius: "18px 18px 0 18px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
            maxWidth: "200px", // Mantenemos el ancho máximo
            width: "auto", // Añadimos ancho automático
            opacity: isFocused ? 1 : 0,
            visibility: isFocused ? "visible" : "hidden",
            transform: isFocused
              ? "translateX(-20px) scale(1)"
              : "translateX(10px) scale(0.95)",
            transition: "all 0.3s ease",
            whiteSpace: "normal", // Cambiamos 'nowrap' a 'normal' para permitir saltos de línea
            wordWrap: "break-word", // Aseguramos que las palabras largas se rompan
            fontSize: "14px",
            fontWeight: 500,
            zIndex: 999,
            "@media (max-width: 600px)": {
              maxWidth: "160px",
              fontSize: "13px",
              right: size + 5, // Más cerca del botón en móviles
            },
            "&:after": {
              content: '""',
              position: "absolute",
              right: "-10px",
              bottom: "0px",
              width: "0",
              height: "0",
              borderLeft: "12px solid #FFFFFF",
              borderTop: "12px solid transparent",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #FF9D00 0%, #FFD700 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline",
                fontWeight: 800,
                marginRight: "4px",
                flexShrink: 0, // Evitamos que "Vic:" se encoja
              }}
            >
              Vic:
            </Box>
            <Box component="span" sx={{ flexGrow: 1 }}>
              {t("chatbot.tooltip", "¡Hola! ¿En qué puedo ayudarte hoy?")}
            </Box>
          </Box>
        </Box>
      </motion.div>
    );
  },
);

export default ChatbotButton;
